import { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorState } from '@ui-kit';
import { BizTable } from '../components/BizTable';
import { CommonFeaturesTab } from './CommonFeaturesTab';
import { MonitoringLinks } from '../components/MonitoringLinks';
import { RecentModulesCard } from '../components/RecentModulesCard';
import { PopularModulesCard } from '../components/PopularModulesCard';
import { WatchedChangesCard } from '../components/WatchedChangesCard';
import { GettingStartedCard } from '../components/GettingStartedCard';
import { useCatalog } from '../hooks/useCatalog';
import { ThemeToggle } from '../../_shared/ThemeToggle';
import { OnboardingTour, useOnboardingTour, ONBOARDING_TOUR_STEPS } from '../../getting-started-guide';

type TabKey = 'biz' | 'features';

function TableSkeleton({ ariaLabel }: { ariaLabel: string }) {
  return (
    <div
      role="status"
      aria-label={ariaLabel}
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          style={{
            height: '52px',
            background: 'var(--color-surface-muted)',
            borderRadius: 'var(--radius-md)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}

export function BizIndexPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  // UAT 2026-06-01 #464: 핵심 가치(공통 기능 재사용)에 맞춰 '전체 공통기능'(features) 탭을 기본 활성
  const [activeTab, setActiveTab] = useState<TabKey>('features');
  const { data, isLoading, isError, refetch } = useCatalog();

  // #686 §R686-1 — 안내 note 의 기능 개수는 데이터 파생만 사용(하드코딩 금지).
  // 로딩/오류/데이터 부재 시 개수 미확정 → 카운트 비의존 문구(messageLoading)로 폴백.
  const bizCount = data?.biz.length ?? null;
  const commonCount = data?.commonFeatures.length ?? null;
  const noteText =
    bizCount != null && commonCount != null
      ? t('index.guide.message', { bizCount, commonCount })
      : t('index.guide.messageLoading');

  // #669 §R669-4 — '투어 다시 보기'(GettingStartedPage) 강제 재시작 신호
  // (`navigate('/_index', { state: { startTour: true } })`) 는 최초 렌더 값만 lazy 로 캡처한다.
  // 아래 effect 가 곧바로 location.state 를 소비(replace)하므로, 컴포넌트 인스턴스가 다시
  // 마운트되기 전까지는 이 값이 유지된다.
  const [forceStartTour] = useState<boolean>(
    () => Boolean((location.state as { startTour?: boolean } | null)?.startTour)
  );
  const tour = useOnboardingTour(forceStartTour);

  useEffect(() => {
    if (!forceStartTour) return;
    // state 소비 — 새로고침/재마운트 시 재트리거 방지(§R669-4)
    navigate(location.pathname, { replace: true, state: {} });
    // 마운트 시 1회만 소비 — forceStartTour 는 마운트 이후 값이 바뀌지 않으므로 deps 는 비운다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // UAT 2026-06-01 #463: '공통 능력 쇼케이스'(common) 탭 제거 — 전용 '능력 카탈로그'(/showcase) 메뉴와 중복
  // UAT 2026-06-01 #464: '전체 공통기능'(features) 탭을 가장 왼쪽(첫 번째)으로 배치
  const TABS: { key: TabKey; label: string }[] = [
    { key: 'features', label: t('index.tabs.features') },
    { key: 'biz',      label: t('index.tabs.biz') },
  ];

  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, key: TabKey) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setActiveTab(key);
      }
    },
    []
  );

  return (
    <div>
      {/* #628 — "시작하기" 최상단 진입 카드 (biz-specs/getting-started-guide.md §6.2) */}
      <GettingStartedCard />

      {/* 페이지 제목 + 테마 토글 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 'var(--spacing-2)',
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--font-size-xl)',
              color: 'var(--color-text-primary)',
            }}
          >
            {t('index.page.title')}
          </h1>
          <p
            style={{
              margin: 'var(--spacing-1) 0 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)',
            }}
          >
            {t('index.page.subtitle')}
          </p>
        </div>
        <ThemeToggle />
      </div>

      {/* 첫 진입 사용자 가이드 */}
      <div
        role="note"
        aria-label={noteText}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          padding: 'var(--spacing-3) var(--spacing-4)',
          marginBottom: 'var(--spacing-4)',
          background: 'var(--color-info-subtle, #EEF2FF)',
          border: '1px solid var(--color-info-border, #C7D2FE)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-info-text, #3730A3)',
        }}
      >
        <span aria-hidden="true" style={{ fontSize: '1rem' }}>i</span>
        <span>{noteText}</span>
      </div>

      {/* Tab Navigation */}
      <div
        role="tablist"
        aria-label={t('index.tablist.aria')}
        style={{
          display: 'flex',
          borderBottom: '1px solid var(--color-surface-border)',
        }}
      >
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.key}`}
              id={`tab-${tab.key}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActiveTab(tab.key)}
              onKeyDown={(e) => handleTabKeyDown(e, tab.key)}
              type="button"
              style={{
                padding: 'var(--spacing-3) var(--spacing-4)',
                border: 'none',
                borderBottom: isActive
                  ? '2px solid var(--color-brand-primary)'
                  : '2px solid transparent',
                background: 'none',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                fontWeight: isActive ? 600 : 400,
                color: isActive
                  ? 'var(--color-brand-primary)'
                  : 'var(--color-text-secondary)',
                marginBottom: '-1px',
                transition: 'color 0.15s ease, border-color 0.15s ease',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Panels */}
      <div style={{ paddingTop: 'var(--spacing-4)' }}>
        {/* Biz Tab */}
        <div
          id="tabpanel-biz"
          role="tabpanel"
          aria-labelledby="tab-biz"
          hidden={activeTab !== 'biz'}
        >
          {activeTab === 'biz' && (
            <>
              {isLoading ? (
                <TableSkeleton ariaLabel={t('index.skeleton.tableLoading')} />
              ) : isError ? (
                <ErrorState
                  title={t('index.bizTable.error.title')}
                  message={t('index.bizTable.error.message')}
                  onRetry={() => refetch()}
                />
              ) : !data || data.biz.length === 0 ? (
                <BizTable rows={[]} />
              ) : (
                <BizTable rows={data.biz} />
              )}
            </>
          )}
        </div>

        {/* Common Features Tab */}
        <div
          id="tabpanel-features"
          role="tabpanel"
          aria-labelledby="tab-features"
          hidden={activeTab !== 'features'}
        >
          {activeTab === 'features' && (
            <>
              {isLoading ? (
                <TableSkeleton ariaLabel={t('index.skeleton.tableLoading')} />
              ) : isError ? (
                <ErrorState
                  title={t('index.bizTable.error.title')}
                  message={t('index.bizTable.error.message')}
                  onRetry={() => refetch()}
                />
              ) : (
                /* UAT 2026-05-20 r2 #462: 관련 biz / biz code 컬럼 제거로 bizItems prop 불필요 */
                <CommonFeaturesTab features={data?.commonFeatures ?? []} />
              )}
            </>
          )}
        </div>
      </div>

      {/* #327 — Operations / Monitoring card */}
      <MonitoringLinks />

      {/* #658 — 최근 변경 모듈 카드 (common-showcase 순수 셀렉터 소비) */}
      <RecentModulesCard />

      {/* #662 — 인기 모듈 카드 (common-showcase useDownloadStats 소비) */}
      <PopularModulesCard />

      {/* #677 — 내 구독 모듈 최근 변경 카드 (common-showcase useWatches + selectWatchedRecentChanges 소비) */}
      <WatchedChangesCard />

      {/* #669 §R669-1 — 온보딩 투어. `_index` 최초 1회 자동시작 + '다시 보기' 강제 재시작 */}
      <OnboardingTour isOpen={tour.isOpen} steps={ONBOARDING_TOUR_STEPS} onDismiss={tour.dismiss} />

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @media (max-width: 768px) {
          #tabpanel-biz [style*="overflowX"],
          #tabpanel-biz table,
          #tabpanel-features [style*="overflowX"],
          #tabpanel-features table {
            font-size: var(--font-size-xs);
          }
        }
      `}</style>
    </div>
  );
}
